import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import {
  ImageElementType,
  InsertionPosition,
  PlacementContainerType,
  PlacementType,
  PriceLocation,
  WidgetElementType,
} from "./widget";

const termsAndConditionsSchema_ = z.object({
  blockCheckoutIfNotAccepted: z.boolean(),
  termsCheckboxSelector: z.string().optional(),
  blockedMessageText: z.string().optional(),
});
export const termsAndConditionsSchema: z.ZodType<TermsAndConditions> =
  termsAndConditionsSchema_;
export interface TermsAndConditions
  extends z.infer<typeof termsAndConditionsSchema_> {}

const clickStandardCheckoutButtonSchema_ = z.object({
  enabled: z.boolean(),
  checkoutButtonSelector: z.string(),
});
export const clickStandardCheckoutButtonSchema: z.ZodType<ClickStandardCheckoutButton> =
  clickStandardCheckoutButtonSchema_;
export interface ClickStandardCheckoutButton
  extends z.infer<typeof clickStandardCheckoutButtonSchema_> {}

const titleSubtitleSchema_ = z.object({
  titleText: z.string().optional(),
  subtitleText: z.string().optional(),
});
export const titleSubtitleSchema: z.ZodType<TitleSubtitle> =
  titleSubtitleSchema_;
export interface TitleSubtitle extends z.infer<typeof titleSubtitleSchema_> {}

const coverageTextSchema_ = z.object({
  returns: titleSubtitleSchema.optional(),
  packageProtection: titleSubtitleSchema.optional(),
  both: titleSubtitleSchema.optional(),
});
export const coverageTextSchema: z.ZodType<CoverageText> = coverageTextSchema_;
export interface CoverageText extends z.infer<typeof coverageTextSchema_> {}

const nonCoverageButtonTextSchema_ = z.object({
  returns: z.string().optional(),
  packageProtection: z.string().optional(),
  both: z.string().optional(),
});
export const nonCoverageButtonTextSchema: z.ZodType<NonCoverageButtonText> =
  nonCoverageButtonTextSchema_;
export interface NonCoverageButtonText
  extends z.infer<typeof nonCoverageButtonTextSchema_> {}

const infoCardStylesSchema_ = z.object({
  color: z.string().optional(),
  titleFontSize: z.number().optional(),
  titleFontColor: z.string().optional(),
  subtitleFontSize: z.number().optional(),
  subtitleFontColor: z.string().optional(),
});
export const infoCardStylesSchema: z.ZodType<InfoCardStyles> =
  infoCardStylesSchema_;
export interface InfoCardStyles extends z.infer<typeof infoCardStylesSchema_> {}

const placementDescriptorSchema_ = z.object({
  cssSelector: z.string(),
  insertionPosition: createEnum(InsertionPosition, {
    enumName: "InsertionPosition",
  }),
  meta: z
    .object({
      displayName: z.string().optional(),
      placementContainerType: createEnum(PlacementContainerType, {
        enumName: "PlacementContainerType",
      }),
    })
    .optional(),
});
export const placementDescriptorSchema: z.ZodType<PlacementDescriptor> =
  placementDescriptorSchema_;
export interface PlacementDescriptor
  extends z.infer<typeof placementDescriptorSchema_> {}

const widgetPlacementsInfoSchema_ = z.object({
  version: z.number(),
  list: z.array(placementDescriptorSchema_),
});
export const widgetPlacementsInfoSchema: z.ZodType<WidgetPlacementsInfo> =
  widgetPlacementsInfoSchema_;
export interface WidgetPlacementsInfo
  extends z.infer<typeof widgetPlacementsInfoSchema_> {}

const baseWidgetConfigSchema_ = z.object({
  name: z.string().optional(),
  placementType: createEnum(PlacementType, {
    enumName: "PlacementType",
  }).optional(),
  /** @deprecated Prefer `placement.list[].insertionPosition`. */
  insertionPosition: createEnum(InsertionPosition, {
    enumName: "InsertionPosition",
  }).optional(),
  /** @deprecated Prefer `placement.list[].cssSelector`. */
  placementSelector: z.string(),
  shadowRootSelector: z.string().optional(),
  dataTarget: z.string().optional(),
  placeInsideAndOutsideShadowRoot: z.boolean().optional(),
  placement: widgetPlacementsInfoSchema_.optional(),
});
export const baseWidgetConfigSchema: z.ZodType<BaseWidgetConfig> =
  baseWidgetConfigSchema_;
export interface BaseWidgetConfig
  extends z.infer<typeof baseWidgetConfigSchema_> {}

const InfoCardWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({
    elementType: z.literal(WidgetElementType.INFO_CARD),
    cardText: coverageTextSchema.optional(),
    showInfoIcon: z.boolean(),
    badgeWord: z.string().optional(),
    infoCardStyles: infoCardStylesSchema.optional(),
    imageElement: createEnum(ImageElementType, {
      enumName: "ImageElementType",
    }).optional(),
    priceLocation: createEnum(PriceLocation, {
      enumName: "PriceLocation",
    }).optional(),
    showBackground: z.boolean().optional(),
    infoButtonCustomAttributes: z.record(z.string(), z.string()).optional(),
  }),
);
export const InfoCardWidgetConfigSchema: z.ZodType<InfoCardWidgetConfig> =
  InfoCardWidgetConfigSchema_;
export interface InfoCardWidgetConfig
  extends z.infer<typeof InfoCardWidgetConfigSchema_> {}

const coverageButtonStylesSchema_ = z.object({
  color: z.string().optional(),
  fontColor: z.string().optional(),
  subtitleFontColor: z.string().optional(),
  fontSize: z.number().optional(),
  subtitleFontSize: z.number().optional(),
  nonCoverageFontColor: z.string().optional(),
  nonCoverageFontSize: z.number().optional(),
  includeNonCoverageUnderline: z.boolean().optional(),
});
export const coverageButtonStylesSchema: z.ZodType<CoverageButtonStyles> =
  coverageButtonStylesSchema_;
export interface CoverageButtonStyles
  extends z.infer<typeof coverageButtonStylesSchema_> {}

const BothCheckoutButtonsWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({
    elementType: z.literal(WidgetElementType.BOTH_CHECKOUT_BUTTONS),
    coverageButtonText: coverageTextSchema.optional(),
    nonCoverageButtonText: nonCoverageButtonTextSchema.optional(),
    coverageButtonStyles: coverageButtonStylesSchema.optional(),
    clickStandardCheckoutButton: clickStandardCheckoutButtonSchema.optional(),
  }),
);
export const BothCheckoutButtonsWidgetConfigSchema: z.ZodType<BothCheckoutButtonsWidgetConfig> =
  BothCheckoutButtonsWidgetConfigSchema_;
export interface BothCheckoutButtonsWidgetConfig
  extends z.infer<typeof BothCheckoutButtonsWidgetConfigSchema_> {}

const IndividualCheckoutButtonsCoverageEnabledWidgetConfigSchema_ =
  baseWidgetConfigSchema_.merge(
    z.object({
      elementType: z.literal(WidgetElementType.COVERAGE_CHECKOUT_BUTTON),
      content: z.string(),
      clickStandardCheckoutButton: clickStandardCheckoutButtonSchema.optional(),
    }),
  );
export const IndividualCheckoutButtonsCoverageEnabledWidgetConfigSchema: z.ZodType<IndividualCheckoutButtonsCoverageEnabledWidgetConfig> =
  IndividualCheckoutButtonsCoverageEnabledWidgetConfigSchema_;
export interface IndividualCheckoutButtonsCoverageEnabledWidgetConfig
  extends z.infer<
    typeof IndividualCheckoutButtonsCoverageEnabledWidgetConfigSchema_
  > {}

const IndividualCheckoutButtonsCoverageDisabledWidgetConfigSchema_ =
  baseWidgetConfigSchema_.merge(
    z.object({
      elementType: z.literal(WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON),
      content: z.string(),
      clickStandardCheckoutButton: clickStandardCheckoutButtonSchema.optional(),
    }),
  );
export const IndividualCheckoutButtonsCoverageDisabledWidgetConfigSchema: z.ZodType<IndividualCheckoutButtonsCoverageDisabledWidgetConfig> =
  IndividualCheckoutButtonsCoverageDisabledWidgetConfigSchema_;
export interface IndividualCheckoutButtonsCoverageDisabledWidgetConfig
  extends z.infer<
    typeof IndividualCheckoutButtonsCoverageDisabledWidgetConfigSchema_
  > {}

const SingleToggleWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({ elementType: z.literal(WidgetElementType.SINGLE_TOGGLE) }),
);
export const SingleToggleWidgetConfigSchema: z.ZodType<SingleToggleWidgetConfig> =
  SingleToggleWidgetConfigSchema_;
export interface SingleToggleWidgetConfig
  extends z.infer<typeof SingleToggleWidgetConfigSchema_> {}

const DoubleToggleWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({ elementType: z.literal(WidgetElementType.DOUBLE_TOGGLE) }),
);
export const DoubleToggleWidgetConfigSchema: z.ZodType<DoubleToggleWidgetConfig> =
  DoubleToggleWidgetConfigSchema_;
export interface DoubleToggleWidgetConfig
  extends z.infer<typeof DoubleToggleWidgetConfigSchema_> {}

const CartCardWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({ elementType: z.literal(WidgetElementType.CART_CARD) }),
);
export const CartCardWidgetConfigSchema: z.ZodType<CartCardWidgetConfig> =
  CartCardWidgetConfigSchema_;
export interface CartCardWidgetConfig
  extends z.infer<typeof CartCardWidgetConfigSchema_> {}

const CheckoutButtonWidgetConfigSchema_ = baseWidgetConfigSchema_.merge(
  z.object({ elementType: z.literal(WidgetElementType.CHECKOUT_BUTTON) }),
);
export const CheckoutButtonWidgetConfigSchema: z.ZodType<CheckoutButtonWidgetConfig> =
  CheckoutButtonWidgetConfigSchema_;
export interface CheckoutButtonWidgetConfig
  extends z.infer<typeof CheckoutButtonWidgetConfigSchema_> {}

const widgetConfigSchema_ = z.discriminatedUnion("elementType", [
  InfoCardWidgetConfigSchema_,
  BothCheckoutButtonsWidgetConfigSchema_,
  IndividualCheckoutButtonsCoverageEnabledWidgetConfigSchema_,
  IndividualCheckoutButtonsCoverageDisabledWidgetConfigSchema_,
  SingleToggleWidgetConfigSchema_,
  DoubleToggleWidgetConfigSchema_,
  CartCardWidgetConfigSchema_,
  CheckoutButtonWidgetConfigSchema_,
]);
export const widgetConfigSchema: z.ZodType<WidgetConfig> = widgetConfigSchema_;
export type WidgetConfig = {} & z.infer<typeof widgetConfigSchema_>;
